import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";

const noop = () => {};

export default function ProjectStoriesOverlay({
  isOpen = false,
  story = null,
  onClose = noop,
  onContentReady = noop,
}) {
  const [activeStory, setActiveStory] = useState(story);
  const closeRef = useRef(null);
  const slideshowRef = useRef(null);
  const copyTimeoutRef = useRef(null);
  const copyButtonRef = useRef(null);

  const hasContent = Boolean(
    activeStory &&
      (activeStory.title ||
        activeStory.location ||
        activeStory.detailsHTML ||
        activeStory.slideshowHTML)
  );

  useEffect(() => {
    if (isOpen && closeRef.current) {
      closeRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (story) {
      setActiveStory(story);
    }
  }, [story]);

  useLayoutEffect(() => {
    if (!isOpen || !activeStory) {
      onContentReady(null);
      return;
    }

    let frame = null;

    const report = () => {
      const container = slideshowRef.current;

      if (container) {
        onContentReady(container);
      } else {
        frame = requestAnimationFrame(report);
      }
    };

    report();

    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }

      onContentReady(null);
    };
  }, [activeStory, isOpen, onContentReady]);

  const handleCopyLink = async () => {
    const url = window.location.href;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      if (copyButtonRef.current) {
        copyButtonRef.current.setAttribute("data-copied", "true");
      }

      copyTimeoutRef.current = window.setTimeout(() => {
        if (copyButtonRef.current) {
          copyButtonRef.current.removeAttribute("data-copied");
        }
        copyTimeoutRef.current = null;
      }, 2000);
    } catch (error) {
      console.error("Unable to copy project link", error);
    }
  };

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    };
  }, []);

  return (
    <Transition
      show={isOpen && hasContent}
      as={Fragment}
      appear
      afterLeave={() => setActiveStory(null)}
    >
      <Dialog as="div" className="b-project-stories__overlay" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-smooth duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-smooth duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="b-project-stories__overlay-backdrop" aria-hidden="true" />
        </Transition.Child>

        <div className="b-project-stories__overlay-wrapper">
          <Transition.Child
            as={Fragment}
            enter="ease-smooth duration-500"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-smooth duration-500"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel className="b-project-stories__overlay-panel">
              <button
                type="button"
                className="b-project-stories__overlay-close"
                onClick={onClose}
                aria-label="Close details"
                ref={closeRef}
              >
                <span aria-hidden="true">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="15"
                    fill="none"
                    viewBox="0 0 16 15"
                  >
                    <path
                      stroke="#000"
                      strokeWidth="2"
                      d="m1.119.707 13.435 13.435M14.142.707.707 14.142"
                    />
                  </svg>
                </span>
              </button>

              <div className="b-project-stories__overlay-body">

                <div className="b-project-stories__overlay-header">
                  {activeStory?.location ? (
                    <span className="b-project-stories__overlay-location">
                      {activeStory.location}
                    </span>
                  ) : null}

                  {activeStory?.title ? (
                    <Dialog.Title className="b-project-stories__overlay-title">
                      {activeStory.title}
                    </Dialog.Title>
                  ) : null}
                </div>
                <div
                  className="b-project-stories__overlay-slideshow"
                  ref={slideshowRef}
                >
                  {activeStory?.slideshowHTML ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: activeStory.slideshowHTML }}
                    />
                  ) : null}
                </div>

                <div className="b-project-stories__overlay-footer">

                  {activeStory?.detailsHTML ? (
                    <div
                      className="b-project-stories__overlay-details"
                      dangerouslySetInnerHTML={{ __html: activeStory.detailsHTML }}
                    />
                  ) : null}

                  <button
                    type="button"
                    className="b-project-stories__overlay-copy"
                    onClick={handleCopyLink}
                    ref={copyButtonRef}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <mask
                        id="project-stories-copy-icon"
                        width="24"
                        height="24"
                        x="0"
                        y="0"
                        maskUnits="userSpaceOnUse"
                        style={{ maskType: "alpha" }}
                      >
                        <path fill="#d9d9d9" d="M0 0h24v24H0z" />
                      </mask>
                      <g mask="url(#project-stories-copy-icon)">
                        <path
                          fill="#1c1b1f"
                          d="M9.058 17.5q-.758 0-1.283-.525a1.75 1.75 0 0 1-.525-1.283V4.308q0-.758.525-1.283T9.058 2.5h8.384q.758 0 1.283.525t.525 1.283v11.384q0 .758-.525 1.283t-1.283.525zm0-1.5h8.384a.3.3 0 0 0 .212-.096.3.3 0 0 0 .096-.212V4.308a.3.3 0 0 0-.096-.212.3.3 0 0 0-.212-.096H9.058a.3.3 0 0 0-.212.096.3.3 0 0 0-.096.212v11.384q0 .116.096.212a.3.3 0 0 0 .212.096m-3.5 5q-.758 0-1.283-.525a1.75 1.75 0 0 1-.525-1.283V6.308h1.5v12.884q0 .116.096.212a.3.3 0 0 0 .212.096h9.884V21z"
                        />
                      </g>
                    </svg>
                    <span>Copy link to this project</span>
                    <span className="b-project-stories__overlay-copy-feedback" aria-live="polite">
                      {activeStory?.copyLinkFeedback || "Copied!"}
                    </span>
                  </button>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
